const REQUIRED_COLUMNS = [
  "ship_from_short_code",
  "ship_to_short_code",
  "ready_time",
  "requested_delivery_time",
] as const;

const OPTIONAL_COLUMNS = [
  "trip_group_id",
  "leg_sequence",
  "billing_party_short_code",
  "trailer_type",
  "trailer_id",
  "tender_id",
  "trip_type",
  "tractor_name",
  "operator_name",
  "operator_2_name",
  "ready_time_end",
  "requested_delivery_time_end",
] as const;

const VALID_TRIP_TYPES = ["NVO", "SVO", "Manual", "CI"];

// Trip-invariant fields that must match across all legs of a multi-leg trip.
// Blank on leg ≥ 2 = inherit from leg 1; equal value = ok; different = reject.
const TRIP_INVARIANT_FIELDS = [
  "billing_party_short_code",
  "trailer_id",
  "tender_id",
  "trip_type",
] as const;

export interface LocationRecord {
  id: string;
  short_code: string;
  customer_id?: string | null;
  street_address?: string;
  city?: string;
  province_state?: string;
  postal_zip_code?: string;
  country?: string;
}

export interface CustomerRecord {
  id: string;
  short_code: string;
  organization_name: string;
}

export interface TractorRecord {
  id: string;
  name: string;
}

export interface DriverRecord {
  id: string;
  name: string;
  email?: string | null;
}

export interface TrailerTypeRecord {
  id: string;
  slug: string;
  label: string;
}

/** Trip-level fields shared by every leg of a parsed trip. */
export interface ParsedTripCommon {
  ship_from_street_address: string;
  ship_from_city: string;
  ship_from_province_state: string;
  ship_from_postal_zip_code: string;
  ship_from_country: string;
  ship_to_street_address: string;
  ship_to_city: string;
  ship_to_province_state: string;
  ship_to_postal_zip_code: string;
  ship_to_country: string;
  ready_time: string;
  requested_delivery_time: string;
  ready_time_end: string;
  requested_delivery_time_end: string;
  billing_party_id: string;
  trailer_id: string;
  trailer_type_id: string;
  tender_id: string;
  trip_type: string;
  assigned_tractor_id: string;
  assigned_operator_id: string;
  assigned_operator_2_id: string;
}

/** Single-leg trip payload. Status is always "booked" downstream. */
export type ParsedOrderRow = ParsedTripCommon;

/** Per-leg payload for a multi-leg trip. */
export interface ParsedLegRow {
  leg_sequence: number;
  ship_from_street_address: string;
  ship_from_city: string;
  ship_from_province_state: string;
  ship_from_postal_zip_code: string;
  ship_from_country: string;
  ship_to_street_address: string;
  ship_to_city: string;
  ship_to_province_state: string;
  ship_to_postal_zip_code: string;
  ship_to_country: string;
  ready_time: string;
  requested_delivery_time: string;
  ready_time_end: string;
  requested_delivery_time_end: string;
  trailer_type_id: string;
  assigned_tractor_id: string;
  assigned_operator_id: string;
  assigned_operator_2_id: string;
  /** Per-leg physical trailer id (short code). Optional — empty when the
   *  CSV row omits it. Downstream write paths fall back to the trip-level
   *  `trailer_id` on the parent when a leg value is missing. */
  trailer_id?: string;
}

export type ParsedTrip =
  | { kind: "single"; row: ParsedOrderRow }
  | { kind: "multi"; parent: ParsedTripCommon; legs: ParsedLegRow[] };

export type ParseResult =
  | { success: true; trips: ParsedTrip[] }
  | { success: false; error: string };

/**
 * Parse a single CSV line respecting quoted fields.
 */
export function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        fields.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
  }
  fields.push(current.trim());
  return fields;
}

/**
 * Split raw CSV text into lines, handling quoted fields that contain newlines.
 */
export function splitCSVLines(text: string): string[] {
  const lines: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          current += '""';
          i++;
        } else {
          inQuotes = false;
          current += char;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
        current += char;
      } else if (char === "\r") {
        if (i + 1 < text.length && text[i + 1] === "\n") {
          continue;
        }
        lines.push(current);
        current = "";
      } else if (char === "\n") {
        lines.push(current);
        current = "";
      } else {
        current += char;
      }
    }
  }
  if (current.length > 0) {
    lines.push(current);
  }
  return lines;
}

interface RawRow {
  /** 1-based row index (header is row 0). */
  rowIndex: number;
  data: Record<string, string>;
}

/**
 * Parse and validate a CSV using short codes, resolving locations, customers, tractors, drivers, and trailer types.
 *
 * Multi-leg trips share a `trip_group_id` and are ordered by `leg_sequence`. Single-leg trips
 * may omit both. Trip-invariant fields (billing_party_short_code, trailer_id, tender_id,
 * trip_type) must match across all legs of a multi-leg trip.
 */
export function parseOrderCSV(
  csvText: string,
  locations: LocationRecord[],
  customers: CustomerRecord[],
  tractors: TractorRecord[] = [],
  drivers: DriverRecord[] = [],
  trailerTypes: TrailerTypeRecord[] = []
): ParseResult {
  if (!csvText || csvText.trim().length === 0) {
    return { success: false, error: "CSV file is empty" };
  }

  const lines = splitCSVLines(csvText).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { success: false, error: "CSV file is empty" };

  const headers = parseCSVLine(lines[0]).map((h) => h.toLowerCase().trim());

  for (const col of REQUIRED_COLUMNS) {
    if (!headers.includes(col)) {
      return { success: false, error: `Missing required column: ${col}` };
    }
  }

  if (lines.length < 2) {
    return { success: false, error: "CSV must contain at least one data row" };
  }

  const locationByCode = new Map<string, LocationRecord>();
  for (const loc of locations) locationByCode.set(loc.short_code.toLowerCase(), loc);
  const customerByCode = new Map<string, CustomerRecord>();
  for (const c of customers) customerByCode.set(c.short_code.toLowerCase(), c);
  const tractorByName = new Map<string, TractorRecord>();
  for (const t of tractors) tractorByName.set(t.name.toLowerCase(), t);
  const driverByName = new Map<string, DriverRecord>();
  for (const d of drivers) driverByName.set(d.name.toLowerCase(), d);
  const driverByEmail = new Map<string, DriverRecord>();
  for (const d of drivers) {
    const email = d.email?.trim().toLowerCase();
    if (email) driverByEmail.set(email, d);
  }
  const trailerTypeBySlug = new Map<string, TrailerTypeRecord>();
  for (const tt of trailerTypes) trailerTypeBySlug.set(tt.slug.toLowerCase(), tt);

  // -- Parse raw rows and group by trip_group_id --
  const rawRows: RawRow[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const data: Record<string, string> = {};
    headers.forEach((h, idx) => {
      data[h] = idx < values.length ? values[idx] : "";
    });
    rawRows.push({ rowIndex: i, data });
  }

  // Group: rows with a non-empty trip_group_id belong together; blank → standalone single-leg.
  const groups = new Map<string, RawRow[]>();
  const singletons: RawRow[] = [];
  for (const r of rawRows) {
    const gid = r.data["trip_group_id"]?.trim() || "";
    if (!gid) {
      singletons.push(r);
    } else {
      const arr = groups.get(gid) ?? [];
      arr.push(r);
      groups.set(gid, arr);
    }
  }

  const trips: ParsedTrip[] = [];

  // Helper: resolve common fields from a row, given context. Returns parsed values or error string.
  function resolveLocations(r: RawRow): { from: LocationRecord; to: LocationRecord } | string {
    for (const col of REQUIRED_COLUMNS) {
      if (!r.data[col]?.trim()) {
        return `Row ${r.rowIndex}: missing required value for "${col}"`;
      }
    }
    const fromCode = r.data["ship_from_short_code"].trim();
    const from = locationByCode.get(fromCode.toLowerCase());
    if (!from) return `Row ${r.rowIndex}: location "${fromCode}" not found`;
    const toCode = r.data["ship_to_short_code"].trim();
    const to = locationByCode.get(toCode.toLowerCase());
    if (!to) return `Row ${r.rowIndex}: location "${toCode}" not found`;
    return { from, to };
  }

  function resolveDates(r: RawRow): {
    ready: Date; delivery: Date; readyEnd: Date; deliveryEnd: Date;
  } | string {
    const ready = new Date(r.data["ready_time"]);
    if (isNaN(ready.getTime())) return `Row ${r.rowIndex}: invalid date for "ready_time"`;
    const delivery = new Date(r.data["requested_delivery_time"]);
    if (isNaN(delivery.getTime())) return `Row ${r.rowIndex}: invalid date for "requested_delivery_time"`;
    let readyEnd = ready;
    const reRaw = r.data["ready_time_end"]?.trim();
    if (reRaw) {
      const p = new Date(reRaw);
      if (isNaN(p.getTime())) return `Row ${r.rowIndex}: invalid date for "ready_time_end"`;
      if (p.getTime() < ready.getTime()) return `Row ${r.rowIndex}: "ready_time_end" must be on or after "ready_time"`;
      readyEnd = p;
    }
    let deliveryEnd = delivery;
    const deRaw = r.data["requested_delivery_time_end"]?.trim();
    if (deRaw) {
      const p = new Date(deRaw);
      if (isNaN(p.getTime())) return `Row ${r.rowIndex}: invalid date for "requested_delivery_time_end"`;
      if (p.getTime() < delivery.getTime()) return `Row ${r.rowIndex}: "requested_delivery_time_end" must be on or after "requested_delivery_time"`;
      deliveryEnd = p;
    }
    return { ready, delivery, readyEnd, deliveryEnd };
  }

  function resolveTrailerType(r: RawRow): string | { id: string } {
    const slug = r.data["trailer_type"]?.trim().toLowerCase() || "";
    if (!slug) return { id: "" };
    const tt = trailerTypeBySlug.get(slug);
    if (!tt) return `Row ${r.rowIndex}: trailer type "${r.data["trailer_type"]?.trim()}" not found`;
    return { id: tt.id };
  }

  function resolveTractor(r: RawRow): string | { id: string } {
    const name = r.data["tractor_name"]?.trim() || "";
    if (!name) return { id: "" };
    const t = tractorByName.get(name.toLowerCase());
    if (!t) return `Row ${r.rowIndex}: tractor "${name}" not found`;
    return { id: t.id };
  }

  function resolveDriver(r: RawRow, col: "operator_name" | "operator_2_name"): string | { id: string } {
    const raw = r.data[col]?.trim() || "";
    if (!raw) return { id: "" };
    const key = raw.toLowerCase();
    const looksLikeEmail = raw.includes("@");
    const d = looksLikeEmail
      ? driverByEmail.get(key) ?? driverByName.get(key)
      : driverByName.get(key) ?? driverByEmail.get(key);
    if (!d) return `Row ${r.rowIndex}: operator "${raw}" not found`;
    return { id: d.id };
  }

  function resolveTripType(r: RawRow): string | { value: string } {
    const v = r.data["trip_type"]?.trim() || "";
    if (!v) return { value: "" };
    if (!VALID_TRIP_TYPES.includes(v)) {
      return `Row ${r.rowIndex}: invalid trip_type "${v}" (expected one of ${VALID_TRIP_TYPES.join(", ")})`;
    }
    return { value: v };
  }

  // -- Single-leg trips (no trip_group_id) --
  for (const r of singletons) {
    const locs = resolveLocations(r);
    if (typeof locs === "string") return { success: false, error: locs };
    const dates = resolveDates(r);
    if (typeof dates === "string") return { success: false, error: dates };
    const trailerType = resolveTrailerType(r);
    if (typeof trailerType === "string") return { success: false, error: trailerType };
    const tractor = resolveTractor(r);
    if (typeof tractor === "string") return { success: false, error: tractor };
    const op = resolveDriver(r, "operator_name");
    if (typeof op === "string") return { success: false, error: op };
    const op2 = resolveDriver(r, "operator_2_name");
    if (typeof op2 === "string") return { success: false, error: op2 };
    const tt = resolveTripType(r);
    if (typeof tt === "string") return { success: false, error: tt };

    const billingCode = r.data["billing_party_short_code"]?.trim();
    if (!billingCode) {
      return { success: false, error: `Row ${r.rowIndex}: missing required value for "billing_party_short_code"` };
    }
    const customer = customerByCode.get(billingCode.toLowerCase());
    if (!customer) return { success: false, error: `Row ${r.rowIndex}: customer "${billingCode}" not found` };

    trips.push({
      kind: "single",
      row: {
        ship_from_street_address: locs.from.street_address || "",
        ship_from_city: locs.from.city || "",
        ship_from_province_state: locs.from.province_state || "",
        ship_from_postal_zip_code: locs.from.postal_zip_code || "",
        ship_from_country: locs.from.country || "",
        ship_to_street_address: locs.to.street_address || "",
        ship_to_city: locs.to.city || "",
        ship_to_province_state: locs.to.province_state || "",
        ship_to_postal_zip_code: locs.to.postal_zip_code || "",
        ship_to_country: locs.to.country || "",
        ready_time: dates.ready.toISOString(),
        requested_delivery_time: dates.delivery.toISOString(),
        ready_time_end: dates.readyEnd.toISOString(),
        requested_delivery_time_end: dates.deliveryEnd.toISOString(),
        billing_party_id: customer.id,
        trailer_id: r.data["trailer_id"]?.trim() || "",
        trailer_type_id: trailerType.id,
        tender_id: r.data["tender_id"]?.trim() || "",
        trip_type: tt.value,
        assigned_tractor_id: tractor.id,
        assigned_operator_id: (op as { id: string }).id,
        assigned_operator_2_id: (op2 as { id: string }).id,
      },
    });
  }

  // -- Multi-leg trips --
  for (const [gid, rowsInGroup] of groups) {
    // Sort by leg_sequence (default 1 when blank).
    const annotated = rowsInGroup.map((r) => {
      const seqRaw = r.data["leg_sequence"]?.trim();
      const seq = seqRaw ? parseInt(seqRaw, 10) : NaN;
      return { row: r, seq };
    });
    for (const a of annotated) {
      if (!Number.isInteger(a.seq) || a.seq < 1) {
        return { success: false, error: `Row ${a.row.rowIndex} (trip ${gid}): leg_sequence must be an integer ≥ 1` };
      }
    }
    annotated.sort((a, b) => a.seq - b.seq);

    // Single-row group → treat as single-leg trip.
    if (annotated.length === 1) {
      const only = annotated[0].row;
      // Reuse the singleton path by pushing into a temporary list? Easier: inline.
      // We synthesize the same logic.
      const locs = resolveLocations(only);
      if (typeof locs === "string") return { success: false, error: locs };
      const dates = resolveDates(only);
      if (typeof dates === "string") return { success: false, error: dates };
      const trailerType = resolveTrailerType(only);
      if (typeof trailerType === "string") return { success: false, error: trailerType };
      const tractor = resolveTractor(only);
      if (typeof tractor === "string") return { success: false, error: tractor };
      const op = resolveDriver(only, "operator_name");
      if (typeof op === "string") return { success: false, error: op };
      const op2 = resolveDriver(only, "operator_2_name");
      if (typeof op2 === "string") return { success: false, error: op2 };
      const tt = resolveTripType(only);
      if (typeof tt === "string") return { success: false, error: tt };

      const billingCode = only.data["billing_party_short_code"]?.trim();
      if (!billingCode) {
        return { success: false, error: `Row ${only.rowIndex} (trip ${gid}): missing required value for "billing_party_short_code"` };
      }
      const customer = customerByCode.get(billingCode.toLowerCase());
      if (!customer) return { success: false, error: `Row ${only.rowIndex}: customer "${billingCode}" not found` };

      trips.push({
        kind: "single",
        row: {
          ship_from_street_address: locs.from.street_address || "",
          ship_from_city: locs.from.city || "",
          ship_from_province_state: locs.from.province_state || "",
          ship_from_postal_zip_code: locs.from.postal_zip_code || "",
          ship_from_country: locs.from.country || "",
          ship_to_street_address: locs.to.street_address || "",
          ship_to_city: locs.to.city || "",
          ship_to_province_state: locs.to.province_state || "",
          ship_to_postal_zip_code: locs.to.postal_zip_code || "",
          ship_to_country: locs.to.country || "",
          ready_time: dates.ready.toISOString(),
          requested_delivery_time: dates.delivery.toISOString(),
          ready_time_end: dates.readyEnd.toISOString(),
          requested_delivery_time_end: dates.deliveryEnd.toISOString(),
          billing_party_id: customer.id,
          trailer_id: only.data["trailer_id"]?.trim() || "",
          trailer_type_id: trailerType.id,
          tender_id: only.data["tender_id"]?.trim() || "",
          trip_type: tt.value,
          assigned_tractor_id: tractor.id,
          assigned_operator_id: (op as { id: string }).id,
          assigned_operator_2_id: (op2 as { id: string }).id,
        },
      });
      continue;
    }

    // Validate contiguous sequence starting at 1.
    for (let i = 0; i < annotated.length; i++) {
      if (annotated[i].seq !== i + 1) {
        return {
          success: false,
          error: `Trip ${gid}: leg_sequence values must be unique and contiguous starting at 1 (got ${annotated.map((a) => a.seq).join(", ")})`,
        };
      }
    }

    // Leg 1 must carry trip-level required fields.
    const leg1 = annotated[0].row;
    const billingCode = leg1.data["billing_party_short_code"]?.trim();
    if (!billingCode) {
      return { success: false, error: `Row ${leg1.rowIndex} (trip ${gid}, leg 1): missing required value for "billing_party_short_code"` };
    }
    const customer = customerByCode.get(billingCode.toLowerCase());
    if (!customer) return { success: false, error: `Row ${leg1.rowIndex} (trip ${gid}): customer "${billingCode}" not found` };

    // Trip-invariant validation: legs ≥ 2 must be blank or match leg 1 exactly.
    const leg1Values: Record<string, string> = {
      billing_party_short_code: billingCode,
      trailer_id: leg1.data["trailer_id"]?.trim() || "",
      tender_id: leg1.data["tender_id"]?.trim() || "",
      trip_type: leg1.data["trip_type"]?.trim() || "",
    };
    for (let i = 1; i < annotated.length; i++) {
      const r = annotated[i].row;
      for (const field of TRIP_INVARIANT_FIELDS) {
        const val = r.data[field]?.trim() || "";
        if (val && val !== leg1Values[field]) {
          return {
            success: false,
            error: `Row ${r.rowIndex} (trip ${gid}, leg ${i + 1}): ${field} "${val}" does not match leg 1 ("${leg1Values[field] || "(blank)"}"). Multi-leg trips must share a single ${field.replace(/_/g, " ")}.`,
          };
        }
      }
    }

    // Resolve leg 1 trip-level assets (defaults for legs that don't override).
    const tripTrailerTypeRes = resolveTrailerType(leg1);
    if (typeof tripTrailerTypeRes === "string") return { success: false, error: tripTrailerTypeRes };
    const tripTractorRes = resolveTractor(leg1);
    if (typeof tripTractorRes === "string") return { success: false, error: tripTractorRes };
    const tripOpRes = resolveDriver(leg1, "operator_name");
    if (typeof tripOpRes === "string") return { success: false, error: tripOpRes };
    const tripOp2Res = resolveDriver(leg1, "operator_2_name");
    if (typeof tripOp2Res === "string") return { success: false, error: tripOp2Res };
    const tripTypeRes = resolveTripType(leg1);
    if (typeof tripTypeRes === "string") return { success: false, error: tripTypeRes };

    // Resolve all legs.
    const parsedLegs: ParsedLegRow[] = [];
    let prevDeliveryMs: number | null = null;
    for (let i = 0; i < annotated.length; i++) {
      const r = annotated[i].row;
      const locs = resolveLocations(r);
      if (typeof locs === "string") return { success: false, error: locs };
      const dates = resolveDates(r);
      if (typeof dates === "string") return { success: false, error: dates };
      const trailerType = resolveTrailerType(r);
      if (typeof trailerType === "string") return { success: false, error: trailerType };
      const tractor = resolveTractor(r);
      if (typeof tractor === "string") return { success: false, error: tractor };
      const op = resolveDriver(r, "operator_name");
      if (typeof op === "string") return { success: false, error: op };
      const op2 = resolveDriver(r, "operator_2_name");
      if (typeof op2 === "string") return { success: false, error: op2 };

      // Per-leg with trip-level fallback.
      const legTrailerTypeId = (trailerType as { id: string }).id || tripTrailerTypeRes.id;
      const legTractorId = (tractor as { id: string }).id || tripTractorRes.id;
      const legOpId = (op as { id: string }).id || tripOpRes.id;
      const legOp2Id = (op2 as { id: string }).id || tripOp2Res.id;

      // Temporal monotonicity across legs.
      if (prevDeliveryMs !== null && dates.ready.getTime() < prevDeliveryMs) {
        return {
          success: false,
          error: `Row ${r.rowIndex} (trip ${gid}, leg ${i + 1}): ready_time must be on or after the previous leg's requested_delivery_time`,
        };
      }
      prevDeliveryMs = dates.delivery.getTime();

      parsedLegs.push({
        leg_sequence: i + 1,
        ship_from_street_address: locs.from.street_address || "",
        ship_from_city: locs.from.city || "",
        ship_from_province_state: locs.from.province_state || "",
        ship_from_postal_zip_code: locs.from.postal_zip_code || "",
        ship_from_country: locs.from.country || "",
        ship_to_street_address: locs.to.street_address || "",
        ship_to_city: locs.to.city || "",
        ship_to_province_state: locs.to.province_state || "",
        ship_to_postal_zip_code: locs.to.postal_zip_code || "",
        ship_to_country: locs.to.country || "",
        ready_time: dates.ready.toISOString(),
        requested_delivery_time: dates.delivery.toISOString(),
        ready_time_end: dates.readyEnd.toISOString(),
        requested_delivery_time_end: dates.deliveryEnd.toISOString(),
        trailer_type_id: legTrailerTypeId,
        assigned_tractor_id: legTractorId,
        assigned_operator_id: legOpId,
        assigned_operator_2_id: legOp2Id,
        // Per-leg trailer_id from the row (empty falls back to the parent
        // trip's leg-1 value when consumers stamp legs).
        trailer_id: r.data["trailer_id"]?.trim() || "",
      });
    }

    const firstLeg = parsedLegs[0];
    const lastLeg = parsedLegs[parsedLegs.length - 1];
    const parent: ParsedTripCommon = {
      ship_from_street_address: firstLeg.ship_from_street_address,
      ship_from_city: firstLeg.ship_from_city,
      ship_from_province_state: firstLeg.ship_from_province_state,
      ship_from_postal_zip_code: firstLeg.ship_from_postal_zip_code,
      ship_from_country: firstLeg.ship_from_country,
      ship_to_street_address: lastLeg.ship_to_street_address,
      ship_to_city: lastLeg.ship_to_city,
      ship_to_province_state: lastLeg.ship_to_province_state,
      ship_to_postal_zip_code: lastLeg.ship_to_postal_zip_code,
      ship_to_country: lastLeg.ship_to_country,
      ready_time: firstLeg.ready_time,
      requested_delivery_time: lastLeg.requested_delivery_time,
      ready_time_end: firstLeg.ready_time_end,
      requested_delivery_time_end: lastLeg.requested_delivery_time_end,
      billing_party_id: customer.id,
      trailer_id: leg1Values.trailer_id,
      trailer_type_id: tripTrailerTypeRes.id,
      tender_id: leg1Values.tender_id,
      trip_type: tripTypeRes.value,
      assigned_tractor_id: tripTractorRes.id,
      assigned_operator_id: tripOpRes.id,
      assigned_operator_2_id: tripOp2Res.id,
    };

    trips.push({ kind: "multi", parent, legs: parsedLegs });
  }

  if (trips.length === 0) {
    return { success: false, error: "CSV must contain at least one data row" };
  }

  return { success: true, trips };
}
